import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Loader2, Inbox, ChevronRight, RefreshCw, MessageSquarePlus, X, Send } from 'lucide-react';
import ticketsService from '@/services/tickets.service';
import api from '@/lib/api';
import { formatDayTime } from '@/lib/format';

const fmtDate = (s?: string) => {
  if (!s) return '';
  try { return formatDayTime(s); } catch { return ''; }
};

interface WhContact { org_id: string; name: string; label?: string }

/* Ultra-basit destek talebi formu — kime (adminin belirlediği liste) + konu + mesaj */
function SupportRequestDialog({ contacts, onClose, onSent }: { contacts: WhContact[]; onClose: () => void; onSent: () => void }) {
  const { t } = useTranslation('warehouse');
  const [targetOrgId, setTargetOrgId] = useState(contacts.length === 1 ? contacts[0].org_id : '');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');

  const sendM = useMutation({
    mutationFn: () => api.post('/tickets/warehouse-request', { targetOrgId, subject: subject.trim(), content: content.trim() }),
    onSuccess: () => { toast.success(t('dialog.sent', 'Anfrage gesendet')); onSent(); onClose(); },
    onError: (e: any) => toast.error(e?.response?.data?.message || t('dialog.sendFailed', 'Senden fehlgeschlagen')),
  });
  const valid = targetOrgId && subject.trim() && content.trim();

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-3 sm:items-center" onClick={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-extrabold text-slate-800">{t('dialog.title', 'Support-Anfrage')}</h2>
          <button onClick={onClose} className="rounded-xl p-2 text-slate-400 hover:bg-slate-100"><X className="h-5 w-5" /></button>
        </div>

        {/* Kime — tek kişiyse otomatik seçili, çoksa KOCAMAN butonlar */}
        {contacts.length > 1 && (
          <div className="mb-3">
            <div className="mb-1.5 text-sm font-bold text-slate-600">{t('dialog.toWhom', 'An wen?')}</div>
            <div className="space-y-2">
              {contacts.map((c) => (
                <button
                  key={c.org_id}
                  onClick={() => setTargetOrgId(c.org_id)}
                  className={`flex w-full items-center justify-between rounded-xl border-2 px-4 py-3 text-left text-base font-bold transition active:scale-[0.99] ${
                    targetOrgId === c.org_id ? 'border-[#0F99FD] bg-[#0F99FD]/5 text-[#0F99FD]' : 'border-slate-200 bg-white text-slate-700'
                  }`}
                >
                  {c.label || c.name}
                  {targetOrgId === c.org_id && <span className="text-sm">✓</span>}
                </button>
              ))}
            </div>
          </div>
        )}
        {contacts.length === 1 && (
          <div className="mb-3 rounded-xl bg-slate-50 px-4 py-2.5 text-sm text-slate-600">
            {t('dialog.to', 'An:')} <span className="font-bold text-slate-800">{contacts[0].label || contacts[0].name}</span>
          </div>
        )}

        <div className="mb-3">
          <div className="mb-1.5 text-sm font-bold text-slate-600">{t('dialog.subject', 'Worum geht es?')}</div>
          <input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t('dialog.subjectPlaceholder', 'z. B. Regal kaputt / Material fehlt')}
            className="w-full rounded-xl border-2 border-slate-200 px-4 py-3 text-base outline-none focus:border-[#0F99FD]"
          />
        </div>

        <div className="mb-4">
          <div className="mb-1.5 text-sm font-bold text-slate-600">{t('dialog.message', 'Nachricht')}</div>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder={t('dialog.messagePlaceholder', 'Beschreiben Sie kurz das Anliegen…')}
            rows={5}
            className="w-full resize-none rounded-xl border-2 border-slate-200 px-4 py-3 text-base outline-none focus:border-[#0F99FD]"
          />
        </div>

        <button
          onClick={() => sendM.mutate()}
          disabled={!valid || sendM.isPending}
          className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0F99FD] py-4 text-lg font-extrabold text-white shadow-md transition active:scale-[0.99] disabled:opacity-50"
        >
          {sendM.isPending ? <Loader2 className="h-6 w-6 animate-spin" /> : <Send className="h-6 w-6" />} {t('dialog.submit', 'Absenden')}
        </button>
      </div>
    </div>
  );
}

export default function WarehouseInbox() {
  const { t } = useTranslation('warehouse');
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['warehouse-tickets'],
    queryFn: () => ticketsService.getTickets({ limit: 100 } as any),
    refetchInterval: 30000,
  });
  const tickets = (data?.data?.tickets || []).filter((x) => x.status !== 'solved' && x.status !== 'closed');

  // Adminin belirlediği yönetici e-postaları (yoksa buton görünmez)
  const { data: contactsData } = useQuery({
    queryKey: ['warehouse-contacts'],
    queryFn: async () => (await api.get('/tickets/warehouse-contacts')).data?.data || [],
    staleTime: 5 * 60 * 1000,
  });
  const contacts: WhContact[] = contactsData || [];

  return (
    <div>
      {/* Destek talebi — EN ÜSTTE, kocaman (admin kişi belirlemişse) */}
      {contacts.length > 0 && (
        <button
          onClick={() => setDialogOpen(true)}
          className="mb-3 flex w-full items-center justify-center gap-2.5 rounded-2xl border-2 border-dashed border-[#0F99FD]/40 bg-[#0F99FD]/5 py-4 text-lg font-extrabold text-[#0F99FD] transition active:scale-[0.99]"
        >
          <MessageSquarePlus className="h-6 w-6" /> {t('inbox.supportRequest', 'Support-Anfrage stellen')}
        </button>
      )}

      <div className="mb-3 flex items-center justify-between px-1">
        <h1 className="text-xl font-extrabold text-slate-800">{t('inbox.tasks', 'Aufgaben ({{count}})', { count: tickets.length })}</h1>
        <button onClick={() => refetch()} className="flex items-center gap-1.5 rounded-xl bg-white px-3 py-2 text-sm font-bold text-slate-600 shadow-sm active:scale-95">
          <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} /> {t('inbox.refresh', 'Aktualisieren')}
        </button>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24 text-slate-400"><Loader2 className="h-8 w-8 animate-spin" /></div>
      ) : tickets.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-200 bg-white py-24 text-center">
          <Inbox className="h-14 w-14 text-slate-300" />
          <p className="mt-4 text-lg font-bold text-slate-500">{t('inbox.emptyTitle', 'Keine offenen Aufgaben')}</p>
          <p className="text-sm text-slate-400">{t('inbox.emptyHint', 'Neue E-Mails erscheinen hier automatisch.')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {tickets.map((tk) => {
            const from = [tk.requester_first_name, tk.requester_last_name].filter(Boolean).join(' ') || tk.requester_email || t('inbox.unknown', 'Unbekannt');
            const isNew = tk.status === 'open';
            return (
              <button
                key={tk.id}
                onClick={() => navigate(`/warehouse/ticket/${tk.id}`)}
                className="flex w-full items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-sm transition active:scale-[0.99]"
              >
                <span className={`mt-0.5 h-3 w-3 shrink-0 rounded-full ${isNew ? 'bg-[#0F99FD]' : 'bg-amber-400'}`} />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-lg font-bold text-slate-800">{tk.subject || t('ticket.noSubject', '(kein Betreff)')}</div>
                  <div className="mt-0.5 truncate text-sm text-slate-500">{from}</div>
                  {(tk as any).internal_target_org_id && (
                    <div className="mt-0.5 inline-flex items-center gap-1 rounded bg-indigo-50 px-1.5 py-0.5 text-[11px] font-bold text-indigo-600">
                      {t('inbox.internalRequest', 'Interne Anfrage')}{(tk as any).internal_target_org_name ? ` → ${(tk as any).internal_target_org_name}` : ''}
                    </div>
                  )}
                  <div className="mt-0.5 text-xs text-slate-400">{fmtDate(tk.created_at)}</div>
                </div>
                <ChevronRight className="h-6 w-6 shrink-0 text-slate-300" />
              </button>
            );
          })}
        </div>
      )}

      {dialogOpen && (
        <SupportRequestDialog
          contacts={contacts}
          onClose={() => setDialogOpen(false)}
          onSent={() => qc.invalidateQueries({ queryKey: ['warehouse-tickets'] })}
        />
      )}
    </div>
  );
}
